/* 6 secondary marketing pages — all share MarketingLayout. */
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight, ArrowUpRight, Check, Brain, Clock, Target, Database,
  Globe2, Bot, Image as ImageIcon, Trophy, Users, Wallet, Activity,
  Cake, GraduationCap, Newspaper, Handshake, Headphones, MessageCircle,
  Phone, Mail, MapPin, Instagram, Sparkles,
} from "lucide-react";
import MarketingLayout, { SmartImg } from "../../components/marketing/MarketingLayout";

const IMG = "/marketing-v2";

const PageHero = ({ over, title, accent, sub }) => (
  <section className="relative pt-36 pb-20 px-6 lg:px-10 mkt-glow-yellow overflow-hidden">
    <div className="max-w-[1400px] mx-auto">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
        className="font-over text-dr-yellow mb-5">{over}</motion.div>
      <motion.h1
        initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.1 }}
        className="font-hero uppercase text-[44px] md:text-[80px] lg:text-[100px] leading-[0.92] mb-6 max-w-5xl"
      >
        {title} {accent && <span className="text-dr-yellow">{accent}</span>}
      </motion.h1>
      {sub && (
        <motion.p
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6, delay: 0.3 }}
          className="text-lg md:text-xl text-white/70 max-w-3xl leading-relaxed"
        >{sub}</motion.p>
      )}
    </div>
  </section>
);

/* ============ OZELLIKLER ============ */
const FEATURES_FULL = [
  { cat: "YÖNETİM", items: [
    [Globe2, "Kurumsal Web Sitesi", "Özel domain · SSL · SEO dostu"],
    [Bot, "AI Maç Analizi", "DR AI Engine · 6 bölüm · 6000+ karakter"],
    [Users, "Kadro Yönetimi", "A takım + akademi · fotoğraflı profil"],
    [Wallet, "Kulüp Maliyesi", "16 kategori · aylık karşılaştırma"],
  ]},
  { cat: "MEDYA", items: [
    [ImageIcon, "9 Instagram Şablonu", "AI görsel · marka renkleri otomatik"],
    [Newspaper, "Blog & Haber", "Kategori · SEO slug · taslak/yayın"],
    [Sparkles, "Doğum Günü AI", "Tek tık özel görsel"],
    [Instagram, "Otomatik Instagram Yazısı", "DR AI Engine Türkçe Instagram yazısı + hashtag"],
  ]},
  { cat: "SPOR OPERASYON", items: [
    [Trophy, "Lig Verisi Sync", "Her Pazar 00:00 otomatik"],
    [Activity, "Antrenman & Yoklama", "Grup bazlı · sebep takibi"],
    [GraduationCap, "Akademi", "Grup · başvuru · aidat"],
    [Target, "Lig Standing", "Otomatik puan durumu"],
  ]},
  { cat: "İŞ MODELİ", items: [
    [Handshake, "Sponsor Yönetimi", "Tier'lı · footer'a yansır"],
    [Cake, "Doğum Günü Takibi", "30 günlük widget"],
    [Headphones, "7/24 Destek", "WhatsApp + telefon"],
    [Brain, "Kredi Sistemi", "Şeffaf · paket bazlı"],
  ]},
];

export const FeaturesPage = () => (
  <MarketingLayout>
    <PageHero
      over="20+ MODÜL"
      title="TÜM ÖZELLİKLER"
      accent="TEK PANEL"
      sub="Kulüp yönetimi için ihtiyacınız olan her şey — dağınık araçlar, 5 farklı abonelik yok. Tek sistem, tek fatura."
    />
    <section className="px-6 lg:px-10 pb-24">
      <div className="max-w-[1400px] mx-auto space-y-16">
        {FEATURES_FULL.map((group, gi) => (
          <div key={group.cat}>
            <div className="flex items-center gap-3 mb-8">
              <span className="mkt-overline-num">{String(gi + 1).padStart(2, "0")}</span>
              <span className="h-[1px] w-10 bg-dr-yellow/40" />
              <h2 className="font-hero uppercase text-2xl text-white">{group.cat}</h2>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {group.items.map(([Icon, t, d], i) => (
                <motion.div
                  key={t}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-50px" }}
                  transition={{ duration: 0.5, delay: i * 0.05 }}
                  className="mkt-card rounded-xl p-6"
                  data-testid={`feature-${gi}-${i}`}
                >
                  <Icon size={22} className="text-dr-yellow mb-4" strokeWidth={2} />
                  <h3 className="font-hero uppercase text-base mb-1.5">{t}</h3>
                  <p className="text-sm text-white/60 leading-relaxed">{d}</p>
                </motion.div>
              ))}
            </div>
          </div>
        ))}

        <div className="text-center pt-10">
          <Link to="/uye-ol" className="mkt-btn-primary" data-testid="features-cta">
            Ücretsiz Üye Ol <ArrowRight size={18} strokeWidth={3} />
          </Link>
        </div>
      </div>
    </section>
  </MarketingLayout>
);

/* ============ AI FUTBOL ============ */
const AI_STEPS = [
  ["Veri Toplama", "Canlı lig verisi + kendi maç geçmişiniz çekilir."],
  ["Kırılım Analizi", "Ev/deplasman performansı, form, golcüler, savunma metrikleri."],
  ["DR AI Engine", "6 bölüm halinde uzun-form rapor oluşturur."],
  ["Taktik Önerisi", "İlk 11 önerisi + skor tahmini + MOTM adayı."],
  ["Uyarı Sistemi", "Kırmızı kutularla otomatik tehlike bildirimi."],
  ["Export", "PDF / WhatsApp paylaşım formatında hazır."],
];

export const AIFutbolPage = () => (
  <MarketingLayout>
    <PageHero
      over="DR AI FUTBOL · MAÇ ÖNÜ ANALİZ"
      title="RAKİBİ TANIMADAN"
      accent="SAHAYA ÇIKMAYIN"
      sub="DR AI Engine + lig verisi + kendi maç verileriniz. 30 saniyede 6 bölümlük profesyonel rapor — BAŞKAN'a hazır."
    />
    <section className="px-6 lg:px-10 pb-24">
      <div className="max-w-[1400px] mx-auto grid lg:grid-cols-2 gap-16 items-center">
        <SmartImg src={`${IMG}/ai-analiz-ipad.png`} alt="AI Maç Analiz" className="w-full rounded-2xl border border-white/10 shadow-2xl" />
        <div>
          <h2 className="mkt-h2 mb-6">AI NASIL <span className="text-dr-yellow">ÇALIŞIR?</span></h2>
          <div className="space-y-4">
            {AI_STEPS.map(([t, d], i) => (
              <motion.div
                key={t}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                className="flex gap-5 items-start"
              >
                <div className="w-10 h-10 rounded-full bg-dr-yellow text-dr-black flex items-center justify-center font-hero text-lg shrink-0">
                  {String(i + 1).padStart(2, "0")}
                </div>
                <div>
                  <div className="font-hero uppercase text-lg mb-1">{t}</div>
                  <div className="text-white/65 text-sm">{d}</div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto mt-20 grid sm:grid-cols-4 gap-4">
        {[
          [Clock, "30 sn", "Rapor süresi"],
          [Brain, "DR AI Engine", "AI motoru"],
          [Database, "Lig Verisi", "Canlı veri"],
          [Target, "6 bölüm", "Yapı"],
        ].map(([Icon, val, lbl]) => (
          <div key={lbl} className="mkt-card rounded-xl p-6 text-center">
            <Icon size={22} className="text-dr-yellow mx-auto mb-3" />
            <div className="font-hero text-2xl">{val}</div>
            <div className="font-over text-white/50 mt-1">{lbl}</div>
          </div>
        ))}
      </div>

      <div className="text-center mt-16">
        <Link to="/uye-ol" className="mkt-btn-primary" data-testid="ai-cta">
          Ücretsiz Üye Ol <ArrowRight size={18} strokeWidth={3} />
        </Link>
      </div>
    </section>
  </MarketingLayout>
);

/* ============ AI SOSYAL MEDYA ============ */
const ALL_TEMPLATES = [
  ["Maç Haftası", "insta-01-match-week.png", "Maç öncesi haftayı duyurun"],
  ["İlk 11 Kadro", "insta-02-lineup.png", "Taktik formasyon görseli"],
  ["Galibiyet", "insta-03-win-score.png", "Maç sonu zafer posteri"],
  ["Mağlubiyet", "insta-04-defeat.png", "Saygılı, comeback vurgulu"],
  ["Doğum Günü", "insta-05-birthday.png", "Oyuncu/personel özel"],
  ["Transfer", "insta-06-transfer.png", "Hoş geldin duyurusu"],
  ["Gol Anı", "insta-07-goal.png", "Skoru atan oyuncu"],
  ["Puan Durumu", "insta-08-standings.png", "Lig tablosu snapshot"],
  ["Antrenman", "insta-09-training.png", "Günlük antrenman duyurusu"],
];

export const SocialMediaPage = () => (
  <MarketingLayout>
    <PageHero
      over="9 ŞABLON · SINIRSIZ VARYASYON"
      title="INSTAGRAM"
      accent="AI ŞABLONLARI"
      sub="Logo, renk, handle, website adresi — hepsi otomatik. Siz sadece veriyi girin, AI profesyonel posteri üretir."
    />
    <section className="px-6 lg:px-10 pb-24">
      <div className="max-w-[1400px] mx-auto grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {ALL_TEMPLATES.map(([t, f, d], i) => (
          <motion.div
            key={f}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: (i % 3) * 0.08 }}
            className="mkt-card rounded-2xl overflow-hidden"
          >
            <SmartImg src={`${IMG}/${f}`} alt={t} className="w-full aspect-square object-cover" loading="lazy" />
            <div className="p-5">
              <div className="font-over text-dr-yellow mb-1">ŞABLON {String(i + 1).padStart(2, "0")}</div>
              <div className="font-hero text-lg uppercase mb-1">{t}</div>
              <div className="text-sm text-white/60">{d}</div>
            </div>
          </motion.div>
        ))}
      </div>

      <div className="text-center mt-16">
        <Link to="/uye-ol" className="mkt-btn-primary" data-testid="social-cta">
          Tüm Şablonları Görelim <ArrowRight size={18} strokeWidth={3} />
        </Link>
      </div>
    </section>
  </MarketingLayout>
);

/* ============ WEB SITESI ============ */
export const WebSitePage = () => (
  <MarketingLayout>
    <PageHero
      over="KURUMSAL WEB SİTESİ"
      title="KULÜBÜNÜZÜN"
      accent="KENDİ SİTESİ"
      sub="Panel + profesyonel kurumsal web sitesi tek paket. Özel domain, SSL, SEO, dark/light mod — her şey dahil."
    />
    <section className="px-6 lg:px-10 pb-24">
      <div className="max-w-[1400px] mx-auto">
        <div className="relative rounded-3xl overflow-hidden border border-white/10 mb-16">
          <SmartImg src={`${IMG}/three-device-website.png`} alt="Kulüp Web Sitesi" className="w-full" />
        </div>

        <div className="grid lg:grid-cols-2 gap-8 mb-16">
          <div className="mkt-card rounded-2xl p-8">
            <div className="font-over text-dr-yellow mb-4">10 HAZIR SAYFA</div>
            <ul className="space-y-2 text-white/80 text-sm">
              {[
                "🏟️ Anasayfa (slider · countdown · haberler)",
                "📅 Fikstür & Sonuçlar",
                "📊 Canlı Puan Durumu",
                "👥 Kadro (A Takım + Akademi)",
                "👔 Teknik Kadro",
                "📰 Haberler · Blog",
                "🎓 Akademi (+ başvuru formu)",
                "⭐ Sponsorlar (tier'lı)",
                "📸 Galeri",
                "📞 İletişim (form + harita)",
              ].map((x) => (<li key={x}>{x}</li>))}
            </ul>
          </div>
          <div className="mkt-card rounded-2xl p-8">
            <div className="font-over text-dr-yellow mb-4">TEKNİK ÖZELLİKLER</div>
            <ul className="space-y-2.5">
              {[
                "Özel domain + SSL",
                "%100 mobil responsive",
                "<2sn açılış süresi",
                "Panel'den marka rengi ayarı",
                "Dark/Light mod",
                "SEO dostu + Open Graph",
                "Otomatik sitemap",
                "Panel değişiklikleri anında yansır",
              ].map((x) => (
                <li key={x} className="flex gap-2.5 text-sm text-white/80">
                  <Check size={16} className="text-dr-yellow shrink-0 mt-0.5" strokeWidth={3} /> {x}
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="text-center">
          <a href="https://livanespor.org" target="_blank" rel="noopener noreferrer" className="mkt-btn-primary mr-3" data-testid="websitesi-example">
            Örnek: livanespor.org <ArrowUpRight size={18} strokeWidth={3} />
          </a>
          <Link to="/uye-ol" className="mkt-btn-ghost" data-testid="websitesi-cta">
            Ücretsiz Üye Ol
          </Link>
        </div>
      </div>
    </section>
  </MarketingLayout>
);

/* ============ ILETISIM ============ */
export const IletisimPage = () => (
  <MarketingLayout>
    <PageHero
      over="İLETİŞİM"
      title="BAĞLANTI"
      accent="KURALIM"
      sub="WhatsApp en hızlısı. Telefonla da ulaşabilirsiniz — üyelik tamamen ücretsiz."
    />
    <section className="px-6 lg:px-10 pb-24">
      <div className="max-w-[1400px] mx-auto grid lg:grid-cols-3 gap-5">
        {[
          [Phone, "Telefon", "+90 543 793 41 01", "tel:+905437934101"],
          [Mail, "E-posta", "info@dijitalroket.com", "mailto:info@dijitalroket.com"],
          [MessageCircle, "WhatsApp", "Hemen yaz →", "https://wa.me/905437934101"],
          [Instagram, "Instagram", "@dijital.roket", "https://instagram.com/dijital.roket"],
          [MapPin, "Konum", "BURSA · Türkiye", "#"],
          [Headphones, "7/24 Destek", "Mevcut müşteriler için", "#"],
        ].map(([Icon, t, v, href], i) => (
          <motion.a
            key={t}
            href={href}
            target={href.startsWith("http") ? "_blank" : undefined}
            rel="noopener noreferrer"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: i * 0.06 }}
            className="mkt-card rounded-2xl p-8 group"
            data-testid={`contact-${i}`}
          >
            <Icon size={26} className="text-dr-yellow mb-5 group-hover:scale-110 transition" />
            <div className="font-over text-white/50 mb-1">{t}</div>
            <div className="font-hero text-xl uppercase text-white group-hover:text-dr-yellow transition">{v}</div>
          </motion.a>
        ))}
      </div>

      <div className="max-w-[1400px] mx-auto mt-16 text-center">
        <Link to="/uye-ol" className="mkt-btn-primary" data-testid="iletisim-cta">
          Ücretsiz Üye Ol <ArrowRight size={18} strokeWidth={3} />
        </Link>
      </div>
    </section>
  </MarketingLayout>
);
